import { ORG_ROLES } from '@hvac/core';
import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/** Membership — the join between a person and an organisation, carrying a role. */
export const orgRoleSchema = z.enum(ORG_ROLES);
export const MEMBERSHIP_STATUSES = ['active', 'revoked'];
export const membershipStatusSchema = z.enum(MEMBERSHIP_STATUSES);
/** The person, as other members of the organisation see them. */
export const memberProfileSchema = z.object({
    userId: uuidSchema,
    email: z.email(),
    fullName: z.string().nullable(),
    avatarUrl: z.url().nullable(),
});
export const membershipSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    role: orgRoleSchema,
    status: membershipStatusSchema,
    profile: memberProfileSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const updateMembershipRoleRequestSchema = z.object({
    role: orgRoleSchema,
});
export const inviteMemberRequestSchema = z.object({
    email: z.email().toLowerCase(),
    role: orgRoleSchema,
});
export const INVITATION_STATUSES = ['pending', 'accepted', 'revoked', 'expired'];
export const invitationStatusSchema = z.enum(INVITATION_STATUSES);
export const invitationSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    email: z.email(),
    role: orgRoleSchema,
    status: invitationStatusSchema,
    expiresAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
});
/** What each role means, in the words a firm administrator chooses between. */
export const ORG_ROLE_LABELS = {
    owner: 'Owner',
    admin: 'Administrator',
    bookkeeper: 'Bookkeeper',
    accountant: 'Accountant',
    technician: 'Technician',
    viewer: 'Viewer',
};
export const ORG_ROLE_DESCRIPTIONS = {
    owner: 'The business. Everything, including who else gets in and whether the books stay.',
    admin: 'Runs the business day to day. Everything except ownership and deleting the business.',
    bookkeeper: 'Prepares the books: reviews documents, codes, matches the bank, signs.',
    accountant: 'Reviews and signs. Reads everything, changes nothing it did not decide.',
    technician: 'In the field. Drops in receipts and sees only what they submitted.',
    viewer: 'Reads the numbers. Changes nothing.',
};
/** The invitation as created: its link is shown once and never stored. */
export const createdInvitationSchema = z.object({
    invitation: invitationSchema,
    inviteUrl: z.url(),
});
/** What a link is for, shown to whoever opens it before they sign in. */
export const invitationPreviewSchema = z.object({
    organizationId: uuidSchema,
    organizationName: z.string(),
    email: z.email(),
    role: orgRoleSchema,
    status: invitationStatusSchema,
    expiresAt: isoDateTimeSchema,
    /** Whether the invited address already has an account: sign in, or sign up. */
    hasAccount: z.boolean(),
});
export const registerWithInvitationRequestSchema = z.object({
    fullName: z.string().trim().min(2, 'Your name, as colleagues know it.').max(120),
    password: z.string().min(10, 'At least ten characters.').max(200),
});
export const acceptedInvitationSchema = z.object({ organizationId: uuidSchema });
//# sourceMappingURL=membership.js.map